import { create } from "zustand";
import api from "../lib/api";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const LOCAL_STORAGE_KEY = "ar.theme";

/**
 * Apply the resolved theme to the document root. The `data-theme` attribute is
 * what the CSS overrides in index.css key off. We also keep `color-scheme` in
 * sync so native form controls (date pickers, scrollbars) match.
 */
function applyResolved(resolved: ResolvedTheme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolved);
  document.documentElement.style.colorScheme = resolved;
}

function systemPrefersLight(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: light)").matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") return systemPrefersLight() ? "light" : "dark";
  return mode;
}

/**
 * Pull whatever theme we last applied locally so first paint matches the
 * user's previous choice — before the backend round-trip on /auth/me lands.
 */
function readStoredMode(): ThemeMode {
  if (typeof localStorage === "undefined") return "dark";
  const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") return raw;
  return "dark";
}

interface ThemeState {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  /** Set + apply + persist locally + push to backend (if signed in). */
  setMode: (mode: ThemeMode, opts?: { sync?: boolean }) => Promise<void>;
  /** Apply a mode loaded from server-side preferences (no echo back). */
  hydrateFromServer: (mode: ThemeMode | undefined | null) => void;
  /** Subscribe to OS scheme changes; only meaningful when mode === "system". */
  watchSystem: () => () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const initialMode = readStoredMode();
  const initialResolved = resolve(initialMode);
  applyResolved(initialResolved);

  return {
    mode: initialMode,
    resolved: initialResolved,

    setMode: async (mode, opts = {}) => {
      const sync = opts.sync ?? true;
      const resolved = resolve(mode);
      set({ mode, resolved });
      applyResolved(resolved);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, mode);
      } catch {
        /* private mode / disabled storage — fine, we just lose persistence */
      }
      if (sync) {
        try {
          await api.put("/auth/me/preferences", { theme: mode });
        } catch {
          // Not signed in or backend unreachable. Local state still wins for
          // this session; the next successful save will reconcile.
        }
      }
    },

    hydrateFromServer: (mode) => {
      if (mode !== "light" && mode !== "dark" && mode !== "system") return;
      if (mode === get().mode) return;
      const resolved = resolve(mode);
      set({ mode, resolved });
      applyResolved(resolved);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, mode);
      } catch { /* ignore */ }
    },

    watchSystem: () => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mq = window.matchMedia("(prefers-color-scheme: light)");
      const handler = () => {
        if (get().mode !== "system") return;
        const resolved = resolve("system");
        set({ resolved });
        applyResolved(resolved);
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    },
  };
});
